/**
 * Google Tag Manager — lazy, runtime loader, same pattern as `googleAnalytics.ts`: `gtm.js` is
 * injected on the FIRST call, never at page load, so the container only runs after the owner has
 * granted marketing consent (the gate lives in `src/components/analytics-consent`). The container
 * id comes from `window.config.gtmId` (baked in `ini.js`; Helm sets it on prod only).
 *
 * Not loaded from index.html on purpose: a boot-time `gtm.js` (1) bypassed the consent gate,
 * (2) let the container's tags read the live URL while it still carried a sign-in / reset
 * credential (`link` / `code`, see `sanitize.ts`) — the caller defers the load until the URL is
 * clean — and (3) when blocked by an ad blocker, tripped index.html's boot-resilience listener
 * (a failed boot-time <script>) into a reload and the "Something went wrong" screen.
 */

// Injected once per page, at module scope so it survives React remounts (router rebuilds).
let injected = false;
// The latest consent decision, so pushes made outside this module can check it (`gtmTracking`).
let granted = false;

/**
 * Push a gtag-style command. It MUST be the real `arguments` object (see `gtagStub` in
 * `googleAnalytics.ts`): GTM / gtag.js read a `dataLayer` entry as a command only in that form.
 */
function pushCommand(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer = window.dataLayer ?? []).push(arguments);
}

/** Consent Mode `update` for the container's Google tags. */
function pushConsent(value: boolean): void {
  const state = value ? 'granted' : 'denied';
  pushCommand('consent', 'update', {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
}

/**
 * Inject gtm.js for `id` once. Idempotent. The current consent goes in BEFORE the `gtm.js`
 * event, so tags firing on the first page see it (call `gtmSetConsent` first).
 */
export function loadGoogleTagManager(id: string): void {
  if (injected || !id) return;
  injected = true;
  pushConsent(granted);
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}

/**
 * Forward the consent decision to the container's tags via a Consent Mode `update`. GTM can't be
 * unloaded, so this is how a withdrawal stops its Google tags (and a later grant resumes them).
 * Pushes nothing until GTM is loaded, so environments without GTM never get a consent command — a pinned
 * 'denied' would also suppress the direct gtag.js tag that shares `window.dataLayer`.
 */
export function gtmSetConsent(value: boolean): void {
  granted = value;
  if (injected) pushConsent(value);
}

/**
 * True while GTM is loaded AND consent is granted. Code that pushes its own events onto
 * `window.dataLayer` (the configurator's `gtmProvider`) checks this: GTM replays the whole queue
 * when it loads and keeps evaluating triggers after a withdrawal, so a push made outside these
 * windows would reach the container's tags without consent.
 */
export function gtmTracking(): boolean {
  return injected && granted;
}
